import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import localforage from 'localforage';
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Button,
    Select,
    FormControl,
    InputLabel,
    MenuItem,
    Chip,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import styles from 'styles/pages/modeles-brand-model.module.scss';
import getModels from 'lib/getModels';
import { urlWriter } from 'tools/functions';
import { apiQl } from 'lib/functions';
import ModelVersions from 'components/modelVersions';
import Breadcrumb from 'components/breadcrumb';
import Link from 'components/link';
import { actionGetBrandsModels, actionSetGetBrandsModelsToNull } from 'store/actions';

const WidgetNav = dynamic(() => import('../../../components/widgetNav'), {
    ssr: false,
});

const WidgetLaunches = dynamic(() => import('../../../components/widgetLaunches'), {
    ssr: false,
});

const WidgetPromo = dynamic(() => import('../../../components/widgetPromotion'), {
    ssr: false,
});

const ModelMaster = dynamic(() => import('../../../components/modelMaster'), {
    ssr: false,
});

const Model = (props) => {
    const { model, dataGetBrandsModels } = props;
    const router = useRouter();
    const [brands, setBrands] = useState(null);
    const [versionSelect, setVersionSelect] = useState(['', '', '']);
    const [selectedVersions, setSelectedVersions] = useState(null);

    useEffect(() => {
        localforage.getItem('brandsModels').then((value) => {
            if (value) {
                if (value.expire > new Date().getTime()) {
                    setBrands(value.data);
                } else {
                    localforage.removeItem('brandsModels');
                }
            } else {
                props.actionGetBrandsModels();
            }
        });
    }, []);

    useEffect(() => {
        if (dataGetBrandsModels) {
            setBrands(dataGetBrandsModels);
            const now = new Date();
            localforage.setItem('brandsModels', {
                data: dataGetBrandsModels,
                expire: now.setDate(now.getDate() + 3),
            });
            props.actionSetGetBrandsModelsToNull();
        }
    }, [dataGetBrandsModels]);

    // reset select on route change
    useEffect(() => {
        const handleRouteChangeStart = () => {
            setVersionSelect(['', '', '']);
        };

        router.events.on('routeChangeStart', handleRouteChangeStart);

        return () => {
            router.events.off('routeChangeStart', handleRouteChangeStart);
        };
    }, [router.events]);
    useEffect(() => {
        setVersionSelect([model.versions[0].id, '', '']);
        setSelectedVersions([model.versions[0]]);
    }, [model]);

    const handleVersionSelectChange = (event) => {
        const index = parseInt(event.target.name.replace('version', ''), 10);
        const versionsSelected = [...versionSelect];
        versionsSelected[index] = event.target.value;
        if (event.target.value === '' && index === 1) {
            versionsSelected[2] = '';
        }
        setVersionSelect([...versionsSelected]);
        let versions = [...selectedVersions];
        if (event.target.value === '') {
            if (index === 1) {
                versions = [versions[0]];
            } else if (index === 2) {
                versions = [versions[0], versions[1]];
            }
        } else {
            const newSelect = model.versions.filter((vs) => {
                return vs.id === event.target.value;
            });
            versions[index] = newSelect[0];
        }
        setSelectedVersions([...versions]);
    };

    const handleSetVersionSelect = () => {
        const handleSetOptions = (i) => {
            let options = [];
            let filtered = [...model.versions];
            if (i > 0) {
                options = [
                    <MenuItem key={0} aria-label="aucune" value="">
                        Aucune
                    </MenuItem>,
                ];
                filtered = filtered.filter((ver) => {
                    return !versionSelect.slice(0, i).includes(ver.id);
                });
            }
            filtered.forEach((vs) => {
                options.push(
                    <MenuItem value={vs.id} key={vs.id}>
                        {vs.version}
                    </MenuItem>,
                );
            });

            return options;
        };
        const selects = [];
        for (let i = 0; i < 3; i++) {
            selects.push(
                <form key={i}>
                    <div className="form_select">
                        <FormControl variant="outlined">
                            <InputLabel id={`version${i}-select-label`}>
                                {`Choisir version ${i === 0 ? 'base' : i}`}
                            </InputLabel>
                            <Select
                                labelId={`version${i}-select-label`}
                                id={`version${i}-select`}
                                name={`version${i}`}
                                label={`Choisir version ${i}`}
                                value={versionSelect[i]}
                                onChange={handleVersionSelectChange}
                                variant="outlined"
                                disabled={i === 2 && versionSelect[1] === ''}
                            >
                                {handleSetOptions(i)}
                            </Select>
                        </FormControl>
                    </div>
                </form>,
            );
        }
        return selects;
    };
    useEffect(() => {
        handleSetVersionSelect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [versionSelect]);

    return (
        <div>
            <Head>
                <title>
                    {`${model.brand.brand} ${model.model} neuve au Maroc | guide d'achat,
                    prix, fiches techniques`}
                </title>
                <meta
                    name="description"
                    content={`${model.brand.brand} ${model.model} neuve au Maroc, guide d'achat, prix, fiches techniques, comparateur, nouveautés`}
                />
                <link
                    rel="canonical"
                    href={`${
                        process.env.NEXT_PUBLIC_CLIENT_HOST
                    }/modeles-voiture/${model.brand.brand.replace(
                        / /g,
                        '-',
                    )}/${model.model.replace(/ /g, '-')}`}
                />
                {/* <!-- Facebook Meta Tags --> */}
                <meta
                    property="og:url"
                    content={`${
                        process.env.NEXT_PUBLIC_CLIENT_HOST
                    }/modeles-voiture/${model.brand.brand.replace(
                        / /g,
                        '-',
                    )}/${model.model.replace(/ /g, '-')}`}
                />
                <meta property="og:type" content="siteweb" />
                <meta
                    property="og:title"
                    content={`${model.brand.brand} ${model.model} neuve au Maroc, prix, fiches techniques`}
                />
                <meta
                    property="og:description"
                    content={`${model.brand.brand} ${model.model} neuve au Maroc, guide d'achat, prix, fiches techniques, comparateur, nouveautés`}
                />
                <meta
                    property="og:image"
                    content={`${process.env.NEXT_PUBLIC_API_HOST}/${model.images[0].filename}`}
                />
                {/* <!-- Twitter Meta Tags --> */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={`${model.brand.brand} ${model.model} neuve au Maroc, prix, fiches techniques`}
                />
                <meta
                    name="twitter:description"
                    content={`${model.brand.brand} ${model.model} neuve au Maroc, guide d'achat, prix, fiches techniques, comparateur, nouveautés`}
                />
                <meta
                    name="twitter:image"
                    content={`${process.env.NEXT_PUBLIC_CLIENT_HOST}/images/models/${model.images[0].filename}`}
                />
            </Head>

            <main className={styles.root}>
                <Breadcrumb
                    links={[
                        {
                            href: `/marques-voiture/${model.brand.brand.replace(
                                / /g,
                                '-',
                            )}`,
                            text: `${model.brand.brand}`,
                        },
                        {
                            href: null,
                            text: `${model.brand.brand} ${model.model}`,
                        },
                    ]}
                />
                <div className="main-title">
                    <h1>{`${model.brand.brand} ${model.model} neuve au Maroc`}</h1>
                </div>
                <div className={styles.mainContainer}>
                    <Card className={styles.cardRoot}>
                        <CardHeader
                            title={<h2>{`Versions ${model.model}`}</h2>}
                            subheader={
                                <Chip label={model.versions.length} color="primary" />
                            }
                        />
                        <CardContent className={styles.cardContent}>
                            <Box className={styles.fiche}>
                                <Link
                                    href={`/fiches-techniques/${urlWriter(
                                        model.brand.brand,
                                    )}`}
                                >
                                    <Button variant="contained" color="primary">
                                        {`Fiches techniques ${model.brand.brand} `}
                                    </Button>
                                </Link>

                                <Link
                                    href={`/images/${urlWriter(
                                        model.brand.brand,
                                    )}/${urlWriter(model.model)}`}
                                >
                                    <Button variant="contained" color="primary">
                                        {`Images 
                                        ${model.brand.brand} ${model.model}`}
                                    </Button>
                                </Link>
                            </Box>
                            <ModelVersions model={model} />
                            <Box className={styles.fiche}>
                                <Link
                                    href={`/fiches-techniques/${urlWriter(
                                        model.brand.brand,
                                    )}`}
                                >
                                    <Button variant="contained" color="primary">
                                        {`Fiches techniques ${model.brand.brand} `}
                                    </Button>
                                </Link>

                                <Link
                                    href={`/images/${urlWriter(
                                        model.brand.brand,
                                    )}/${urlWriter(model.model)}`}
                                >
                                    <Button variant="contained" color="primary">
                                        {`Images 
                                        ${model.brand.brand} ${model.model}`}
                                    </Button>
                                </Link>
                            </Box>
                        </CardContent>
                    </Card>
                    <Card className={styles.cardRoot}>
                        <CardHeader
                            title={<h2>Sélectionnez versions pour comparaison</h2>}
                        />
                        <CardContent className={styles.cardContent}>
                            <div className={styles.selectForms}>
                                {handleSetVersionSelect()}
                            </div>
                        </CardContent>
                    </Card>
                    {selectedVersions && (
                        <ModelMaster model={model} selectedVersions={selectedVersions} />
                    )}
                    {brands && (
                        <>
                            <WidgetPromo data={brands} />
                            <WidgetNav brands={brands} />
                            <WidgetLaunches data={brands} />
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

Model.propTypes = {
    model: PropTypes.object.isRequired,
    dataGetBrandsModels: PropTypes.any,
    actionGetBrandsModels: PropTypes.func.isRequired,
    actionSetGetBrandsModelsToNull: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
    return {
        dataGetBrandsModels: state.brand.dataGetBrandsModels,
    };
};

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            actionGetBrandsModels,
            actionSetGetBrandsModelsToNull,
        },
        dispatch,
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(Model);

const queryQl = `query getModel(
  	$model: [String!]
) {
    models(
      model_list: $model
      isActive: true
    ) {
        id
    	model
    	modelYear
    	images(isFeatured: true) {
            filename
        }
    	brand {
            id
            brand
            image
        }
        versions(
            isActive: true
            _order:{ bodyType: "ASC", motor_fuel: "ASC", prices_price: "ASC"}
        ){
            id
            version
          	gearbox
          	places
          	doors
          	curbWeight
          	gvw
          	traction
      		tyreFront {
                tyre
            }
            tyreBack {
                tyre
            }
            prices(
                _order: {createdAt: "DESC"}
            ) {
                edges {
                    node {
                        id
                        createdAt
                        price
                        promo
                    }
                }
            }
            CF {
                CF
            }
        	motor {
                power
                fuel
                cc
                cylinder
                torque
                valves
                aspiration
            }
      		measures {
                fuelTank
                width
                height
                length
                wheelbase
                trunk
                trunkMax
            }
            performance {
                to100
                maxSpeed
                emissions
                mileageCity
                mileageRoad
                mileageMix
            }
        }
    }
}`;

const queryQlTrims = `query getModelTrims(
  	$model: [String!]
) {
    models(
      model_list: $model
      isActive: true
    ) {
        id
        versions(
            isActive: true
            _order:{ bodyType: "ASC", motor_fuel: "ASC", prices_price: "ASC"}
        ){
            id
            trims(_order: { trim: "ASC"}) {
                id
                trim
                trimType
            }
        }
    }
}`;

export async function getStaticPaths() {
    let models = await getModels();
    models = models.data.models;
    const paths = [];
    models.forEach((model) => {
        paths.push({
            params: {
                brand: model.brand.brand.replace(/ /g, '-'),
                model: model.model.replace(/ /g, '-'),
            },
        });
    });
    return {
        paths,
        fallback: false,
    };
}

export async function getStaticProps({ params }) {
    const { brand: brandParam, model: modelParam } = params;
    let t = 0;
    const variables = {
        model: [
            modelParam,
            modelParam.replace(/-/g, ' '),
            modelParam.replace(/-/g, (match) => (++t === 2 ? ' ' : match)), // this to accommodate e-tron Sportback
        ],
        // after: getBaseDate(90),
    };
    const dataMain = await apiQl(queryQl, variables, false);
    const dataTrims = await apiQl(queryQlTrims, variables, false);
    dataMain.data.models[0].versions.forEach((version) => {
        const vers = dataTrims.data.models[0].versions.filter((ver) => {
            return ver.id === version.id;
        });
        // eslint-disable-next-line no-param-reassign
        version.trims = vers[0].trims;
    });
    const modelFilter = dataMain.data.models.filter((mod) => {
        return (
            mod.model.toLowerCase().replace(/-/g, ' ') ===
                modelParam.toLowerCase().replace(/-/g, ' ') &&
            mod.brand.brand.toLowerCase() === brandParam.toLowerCase().replace(/-/g, ' ')
        );
    });
    return {
        props: {
            model: modelFilter[0],
        },
    };
    /*
    const { model: modelParam } = params;
    let models = await getModels();
    models = models.data.models;
    const modelFilter = models.filter((mod) => {
        return mod.model.replace(/ /g, '-') === modelParam;
    });
    let posts = await getPosts();
    posts = posts.data.posts;
    // data for widgets
    let brands = await getBrandsModels();
    brands = brands.data.brands;
    const randMod = randIndex(19, 6);
    const recentModels = models.filter((model, ind) => {
        return randMod.includes(ind);
    });
    // rand promos
    const promos = [];
    models.forEach((model) => {
        const promoVersions = model.versions.filter((version) => {
            return version.prices[0].promo !== null;
        });
        if (promoVersions.length > 0) {
            // eslint-disable-next-line no-param-reassign
            model.versions = promoVersions;
            promos.push(model);
        }
    });
    const selectedPromos = randIndex(promos.length, 6);
    const randPromos = promos.filter((model, ind) => {
        return selectedPromos.includes(ind + 1);
    });

    const variables = {
        id: modelFilter[0].id,
        after: getBaseDate(90),
    };
    const data = await apiQl(queryQl, variables, false);
    const model = data.data.model;
    return {
        props: {
            model,
            brands,
            posts,
            recentModels,
            randPromos,
        },
    };
    */
}
